import { useEffect, useState } from 'react';
import sanitize from 'sanitize-html';

import SortSelect from 'components/search/SortSelect/SortSelect';
import { cn } from 'helpers/classnames';
import { retrieveH1TagFromFilters } from 'helpers/SeoOptimizedDataHelper';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import useMartyContext from 'hooks/useMartyContext';
import { getRedirectedVerbiage, resetSearchFilter } from 'helpers/SearchUtils';
import { BannerAdInTest } from 'components/common/BannerAd/BannerAd';

import css from 'styles/components/search/searchHeader.scss';

export const SearchHeader = ({ totalProductCount, onSortSelected, filters, searchFilter, isResponseFilter, searchTerm, searchHeaderRef }) => {
  const {
    testId,
    marketplace: {
      search: { useAutoCorrect }
    }
  } = useMartyContext();
  const [redirectVerbiage, setRedirectVerbiage] = useState(null);
  const { sort, autocorrect, honeTag } = filters;
  const shouldShowAutocorrect = useAutoCorrect && autocorrect?.termBeforeAutocorrect;

  const headerProps = {
    'className': cn(css.heading, 'zappos:heading-l', 'lg:zappos:heading-xl !mb-2 max-sm:zappos:!heading-s max-md:!mb-0'),
    'data-test-id': testId('searchResultsText')
  };

  let subHeadingText = !!totalProductCount && `${totalProductCount} items found`;

  let pageMainHeader = (
    <h1
      {...headerProps}
      dangerouslySetInnerHTML={{
        __html: sanitize(retrieveH1TagFromFilters(filters))
      }}
    />
  );

  if (shouldShowAutocorrect) {
    const headingContent = (
      <>
        Showing {totalProductCount} Results for <strong>"{honeTag}"</strong>
      </>
    );

    pageMainHeader = !!totalProductCount && <h1 {...headerProps}>{headingContent}</h1>;
    subHeadingText = autocorrect.termBeforeAutocorrect ? (
      <>
        We couldn't find an exact result for <strong>"{autocorrect.termBeforeAutocorrect}"</strong>
      </>
    ) : (
      ''
    );
  }

  useEffect(() => {
    const redirectedVerbiage = getRedirectedVerbiage(searchFilter, isResponseFilter);
    if (redirectedVerbiage) {
      setRedirectVerbiage(redirectedVerbiage);
    }
  }, []);

  // once redirect verbiage gets set lets reset it in local storage
  useEffect(() => {
    if (isResponseFilter && redirectVerbiage) {
      resetSearchFilter();
    }
  }, [redirectVerbiage]);

  const isBestForYouSortEligible = filters.bestForYouSortEligible;

  return (
    <>
      <div className="searchPage">
        <BannerAdInTest className="my-8" position="top" />
      </div>
      <div className={'searchPage mt-2 flex flex-col items-start md:flex-row md:justify-between md:gap-4 lg:gap-0'} ref={searchHeaderRef}>
        <div
          className={cn('min-w-[222px] md:w-full', {
            [css.corrected]: shouldShowAutocorrect
          })}
        >
          {pageMainHeader}
          <div>
            {redirectVerbiage && (
              <div className={css.redirectVerbiageContainer} data-test-id={testId('redirectedSearchFilter')}>
                <p className={css.redirectVerbiageText}>
                  We couldn't find "{searchTerm}" in {redirectVerbiage} category
                </p>
                <p className={css.redirectVerbiageText}>Here are some suggestions for {searchTerm} in All departments</p>
              </div>
            )}
            <span className={'body-xs-light text-gray-500 lg:body-m-light max-sm:hidden'} data-test-id={testId('searchResultsItemCount')}>
              {subHeadingText}
            </span>
          </div>
        </div>

        <div className="mb-7 max-[768px]:hidden">
          <SortSelect isBestForYouSortEligible={!!isBestForYouSortEligible} onSortSelected={onSortSelected} selectedSort={sort} />
        </div>
      </div>
    </>
  );
};

SearchHeader.displayName = 'SearchHeader';
export default withErrorBoundary('SearchHeader', SearchHeader);
